// src/services/oauth.js
import { ref } from 'vue'
import { setAuthToken, currentUser, isLoading, authError } from './auth'
import { fetchProviderProfile } from './provider'
import router from '@/router' // Import your router

// Create reactive state for OAuth
export const oauthLoading = ref(false)
export const oauthError = ref(null)

// Base URL for API
const API_BASE_URL = import.meta.env.VITE_API_URL
const API_KEY = import.meta.env.VITE_API_KEY

/**
 * Initiate OAuth flow for Google
 * Opens the Google OAuth consent screen in a new window
 * @param {string} role - The user role (required)
 */
export async function initiateGoogleOAuth(role) {
  return initiateOAuth('google', role)
}

/**
 * Initiate OAuth flow for Apple
 * Opens the Apple OAuth consent screen in a new window
 * @param {string} role - The user role (required)
 */
export async function initiateAppleOAuth(role) {
  return initiateOAuth('apple', role)
}

/**
 * Initiate OAuth flow for Microsoft
 * Opens the Microsoft OAuth consent screen in a new window
 * @param {string} role - The user role (required)
 */
export async function initiateMicrosoftOAuth(role) {
  return initiateOAuth('microsoft', role)
}

/**
 * Initiate OAuth flow for GitHub
 * Opens the GitHub OAuth consent screen in a new window
 * @param {string} role - The user role (required)
 */
export async function initiateGitHubOAuth(role) {
  return initiateOAuth('github', role)
}

/**
 * Initiate OAuth flow for Yahoo
 * Opens the Yahoo OAuth consent screen in a new window
 * @param {string} role - The user role (required)
 */
export async function initiateYahooOAuth(role) {
  return initiateOAuth('yahoo', role)
}

/**
 * Generic function to initiate OAuth flow for any provider
 * @param {string} provider - The OAuth provider (google, microsoft, github, yahoo)
 * @param {string} role - The user role (required)
 */
async function initiateOAuth(provider, role) {
  try {
    oauthLoading.value = true
    oauthError.value = null

    // Validate role is provided
    if (!role) {
      throw new Error('Role must be specified to initiate authentication')
    }

    // Validate role is valid
    if (!['patient', 'provider', 'admin'].includes(role)) {
      throw new Error(`Invalid role: ${role}. Must be 'patient', 'provider', or 'admin'`)
    }

    // Store role in localStorage for the callback
    localStorage.setItem('oauth_pending_role', role)

    // Get language preference - this can have a default since it's not security-critical
    const language = localStorage.getItem('oauth_pending_language') || 'english'


    // Get the OAuth URL from the backend
    const response = await fetch(`${API_BASE_URL}/api/auth/oauth/${provider}?role=${role}&language=${language}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to initiate ${provider} OAuth: ${errorText}`)
    }

    const data = await response.json()

    if (!data.auth_url) {
      throw new Error(`No auth URL returned for ${provider} OAuth`)
    }

    // Open the OAuth URL in a popup window
    const width = 600
    const height = 700
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(
      data.auth_url,
      `${provider}-oauth`,
      `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
    )

    if (!popup) {
      throw new Error('Popup blocked! Please allow popups for this website.')
    }

    // Set up message listener for the OAuth callback
    return new Promise((resolve, reject) => {
      const messageHandler = async (event) => {
        // Only handle messages from our expected origin
        if (!event.origin.startsWith(window.location.origin)) {
          return
        }

        try {
          const { type, data: messageData } = event.data

          console.log("event.data => ", event.data);

          if (type === 'oauth-callback' && messageData) {
            window.removeEventListener('message', messageHandler)

            if (messageData.error) {
              reject(new Error(messageData.error))
              return
            }

            if (messageData.token) {
              // Set the auth token
              setAuthToken(messageData.token)

              // Store user data if provided
              if (messageData.user) {
                currentUser.value = messageData.user

                localStorage.setItem('user_data', JSON.stringify(messageData.user));
                localStorage.setItem('user_data_timestamp', Date.now().toString());

                // Store the language preference
                if (messageData.user.preferred_language) {
                  localStorage.setItem('user_language', messageData.user.preferred_language)
                  localStorage.setItem('languageSelectionCompleted', 'true')
                }

                // Store the role
                if (messageData.user.role) {
                  localStorage.setItem('user_role', messageData.user.role)
                }

                // Clear the pending OAuth values
                localStorage.removeItem('oauth_pending_role')
                localStorage.removeItem('oauth_pending_language')
              }

              // Redirect based on user role
              setTimeout(async () => {
                if (messageData.user && messageData.user.role) {
                  switch (messageData.user.role) {
                    case 'provider':
                      // Check if provider profile exists before redirecting
                      try {
                        console.log("=== Provider login flow ===");
                        console.log("localStorage auth_token:", localStorage.getItem('auth_token'));
                        console.log("messageData.token:", messageData.token);

                        const token = localStorage.getItem('auth_token') || messageData.token;
                        console.log("Using token:", token ? token.substring(0, 20) + "..." : "NO TOKEN");

                        const profileData = await fetchProviderProfile(token);
                        console.log("profileData:", profileData);

                        if (profileData.status === 'not_found') {
                          router.push('/provider/manage');
                        } else {
                          router.push('/provider-profile');
                        }
                      } catch (error) {
                        console.error("Error fetching provider profile:", error);
                        router.push('/provider/manage');
                      }
                      break;
                      break;
                    case 'admin':
                      router.push('/admin-dashboard');
                      break;
                    case 'patient':
                      router.push('/patient-profile');
                      break;
                    default:
                      router.push('/');
                  }
                } else {
                  router.push('/');
                }
              }, 500);

              resolve(messageData)
            } else {
              reject(new Error('No token received from OAuth process'))
            }
          }
        } catch (error) {
          console.error('Error processing OAuth message:', error)
          reject(error)
        }
      }

      window.addEventListener('message', messageHandler)

      // Set a timeout to reject the promise if the popup is closed without completing
      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopupClosed)
          window.removeEventListener('message', messageHandler)
          reject(new Error('OAuth popup was closed before authentication completed'))
        }
      }, 1000)
    })
  } catch (error) {
    console.error(`${provider} OAuth error:`, error)
    oauthError.value = error.message
    throw error
  } finally {
    oauthLoading.value = false
  }
}

/**
 * Handle OAuth callback (used in the callback page that receives the OAuth response)
 * Extracts data from URL and sends it to the parent window
 */
/**
 * Handle OAuth callback (used in the callback page that receives the OAuth response)
 * Extracts data from URL and sends it to the parent window
 */
export async function handleOAuthCallback() {
  try {
    // Extract provider from URL path
    const pathParts = window.location.pathname.split('/');
    const oauthIndex = pathParts.findIndex(part => part === 'oauth');
    const provider = oauthIndex >= 0 && oauthIndex + 1 < pathParts.length
      ? pathParts[oauthIndex + 1]
      : null;

    if (!provider) {
      const errorMsg = 'Could not determine OAuth provider from URL';
      if (window.opener) {
        window.opener.postMessage({
          type: 'oauth-callback',
          data: { error: errorMsg }
        }, window.location.origin);
      }
      throw new Error(errorMsg);
    }

    // Check if this is Apple OAuth
    if (provider === 'apple') {

      // First check query parameters (in case this is a GET request)
      const urlParams = new URLSearchParams(window.location.search);
      let code = urlParams.get('code');
      let state = urlParams.get('state');
      let error = urlParams.get('error');

      // If not in URL params, try to extract from form or body (for POST)
      if (!code) {

        // Apple can use form_post response mode which sends the data via POST
        // Try to access the form fields directly if they exist
        const appleForm = document.getElementById('apple-oauth-form');
        const appleCodeInput = document.getElementById('apple-code');

        // Check if we have hidden form inputs with data
        if (appleForm && appleCodeInput && appleCodeInput.value) {
          code = appleCodeInput.value;
          state = document.getElementById('apple-state')?.value;
          error = document.getElementById('apple-error')?.value;
        } else {
          // Try to parse raw data from document body for Apple's POST
          const bodyText = document.body.innerText || '';

          // Check for JSON format first
          if (bodyText.startsWith('{') && bodyText.endsWith('}')) {
            try {
              const jsonData = JSON.parse(bodyText);
              code = jsonData.code;
              state = jsonData.state;
              error = jsonData.error;
            } catch (e) {
              console.log('Body content is not valid JSON');
            }
          }

          // Try form-urlencoded format
          if (!code && bodyText.includes('code=')) {
            try {
              // Convert body text to form params
              const formParams = new URLSearchParams(bodyText);
              code = formParams.get('code');
              state = formParams.get('state');
              error = formParams.get('error');
            } catch (e) {
              console.log('Failed to parse form data from body', e);
            }
          }

          // If still no code, check for any inputs in the document
          if (!code) {
            const inputs = document.querySelectorAll('input');
            for (const input of inputs) {
              if (input.name === 'code' && input.value) {
                code = input.value;
              }
              if (input.name === 'state' && input.value) {
                state = input.value;
              }
              if (input.name === 'error' && input.value) {
                error = input.value;
              }
            }
          }
        }
      }

      if (error) {
        const errorMsg = `Error from Apple: ${error}`;
        console.error(errorMsg);
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth-callback',
            data: { error: errorMsg }
          }, window.location.origin);
        }
        throw new Error(errorMsg);
      }

      if (!code) {
        // If we still couldn't find the code, report the error
        const errorMsg = 'No authorization code received from Apple';
        console.error(errorMsg);
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth-callback',
            data: { error: errorMsg }
          }, window.location.origin);
        }
        throw new Error(errorMsg);
      }

      // Now we have the code, complete the OAuth process with your backend
      const userData = await completeAppleOAuth(code, state);

      if (!userData || !userData.token) {
        const errorMsg = 'No token received from Apple OAuth';
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth-callback',
            data: { error: errorMsg }
          }, window.location.origin);
        }
        throw new Error(errorMsg);
      }

      // Send the user data and token to the parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'oauth-callback',
          data: userData
        }, window.location.origin);
        return userData;
      } else {
        throw new Error('Authentication window not connected to parent window');
      }
    } else {
      // For other providers that use GET method:
      // Parse URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      if (error) {
        // If there's an error, send it to the parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth-callback',
            data: { error: errorDescription || error }
          }, window.location.origin);
        }
        throw new Error(errorDescription || error);
      }

      if (!code) {
        const errorMsg = 'No authorization code received';
        // If there's no code, send an error to the parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth-callback',
            data: { error: errorMsg }
          }, window.location.origin);
        }
        throw new Error(errorMsg);
      }

      // Exchange the code for a token using the standard method
      const userData = await completeOAuth(provider, code, urlParams.get('state'));

      // Verify we got a token
      if (!userData || !userData.token) {
        const errorMsg = `No token received from ${provider} OAuth`;
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth-callback',
            data: { error: errorMsg }
          }, window.location.origin);
        }
        throw new Error(errorMsg);
      }

      // Send the user data and token to the parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'oauth-callback',
          data: userData
        }, window.location.origin);
        return userData;
      } else {
        console.error('No opener window found for OAuth callback');
        throw new Error('Authentication window not connected to parent window');
      }
    }
  } catch (error) {
    console.error('Error handling OAuth callback:', error);
    // If there's an error, send it to the parent window if possible
    if (window.opener) {
      window.opener.postMessage({
        type: 'oauth-callback',
        data: { error: error.message }
      }, window.location.origin);
    }
    throw error;
  }
}

/**
 * Complete the OAuth flow by sending the code to the backend
 * @param {string} provider - The OAuth provider (google, microsoft, github, yahoo)
 * @param {string} code - The authorization code from the OAuth provider
 * @param {string} state - The state parameter for security validation
 * @returns {Promise<Object>} - The user data and token
 */
async function completeOAuth(provider, code, state) {
  try {
    // Call the callback endpoint with the code and state
    const response = await fetch(`${API_BASE_URL}/api/auth/oauth/${provider}/callback?code=${code}&state=${state || ''}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    // Clear localStorage after use
    localStorage.removeItem('oauth_pending_role')
    localStorage.removeItem('oauth_pending_language')

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to complete ${provider} OAuth: ${errorText}`)
    }

    const data = await response.json()

    if (!data.token) {
      throw new Error(`No token received from ${provider} OAuth`)
    }

    return data
  } catch (error) {
    console.error(`Error completing ${provider} OAuth:`, error)
    throw error
  }
}

/**
 * Complete the Apple OAuth flow by POSTing the code to the backend
 * @param {string} code - The authorization code from Apple
 * @param {string} state - The state parameter for security validation
 * @param {string} user - Optional user data from Apple
 * @returns {Promise<Object>} - The user data and token
 */
async function completeAppleOAuth(code, state, user) {
  try {
    // For Apple, we need to use a FormData object to POST the data
    const formData = new FormData();
    formData.append('code', code);
    if (state) formData.append('state', state);
    if (user) formData.append('user', user);

    // Call the callback endpoint with a POST request
    const response = await fetch(`${API_BASE_URL}/api/auth/oauth/apple/callback`, {
      method: 'POST',
      headers: {
        'X-API-Key': API_KEY
      },
      body: formData
    });

    // Clear localStorage after use
    localStorage.removeItem('oauth_pending_role');
    localStorage.removeItem('oauth_pending_language');

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to complete Apple OAuth: ${errorText}`);
    }

    const data = await response.json();

    if (!data.token) {
      throw new Error('No token received from Apple OAuth');
    }

    return data;
  } catch (error) {
    console.error('Error completing Apple OAuth:', error);
    throw error;
  }
}

export default {
  initiateGoogleOAuth,
  initiateMicrosoftOAuth,
  initiateGitHubOAuth,
  initiateYahooOAuth,
  initiateAppleOAuth,
  handleOAuthCallback,
  completeOAuth,
  completeAppleOAuth
}